<template>
    <div class="w-full">
        <div class="flex flex-wrap justify-between">
            <div class="md:w-1/2 w-full px-3 mt-5">
                <span class="font-semibold text-lg block mb-2">
                    {{ $t("json.input") }}
                </span>
                <code-editor v-model:value="input" />
            </div>

            <div class="w-full md:hidden flex justify-center mt-4 px-3">
                <button
                    :disabled="isProcessing"
                    @click="processFile"
                    class="flex items-center gap-2 border border-primary-primary hover:bg-white bg-primary-primary text-white hover:text-primary-primary font-bold py-2 px-6 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                    <span>
                        {{
                            isProcessing
                                ? $t("common.processing")
                                : $t("common.process")
                        }}
                    </span>
                </button>
            </div>

            <div id="json-formatter-result" class="md:w-1/2 w-full px-3 mt-5">
                <div class="flex flex-col">
                    <span class="font-semibold text-lg mb-2">{{
                        $t("json.output")
                    }}</span>
                    <span
                        v-if="reasonForInvalidity"
                        class="text-yellow-500 text-base mt-2 md:hidden"
                    >
                        {{ $t("json.invalid") }}: {{ reasonForInvalidity }}
                    </span>
                </div>
                <code-editor v-model:value="result" read-only />
            </div>
        </div>

        <div class="hidden md:flex flex-col items-center px-3 mt-6 space-y-2">
            <button
                :disabled="isProcessing"
                @click="processFile"
                class="flex items-center gap-2 border border-primary-primary hover:bg-white bg-primary-primary text-white hover:text-primary-primary font-bold py-2 px-6 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
                <svg
                    v-if="isProcessing"
                    class="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    />
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                    />
                </svg>
                <span>
                    {{
                        isProcessing
                            ? $t("common.processing")
                            : $t("common.process")
                    }}</span
                >
            </button>

            <span
                v-if="reasonForInvalidity"
                class="text-yellow-500 text-base text-center"
            >
                JSON is invalid: {{ reasonForInvalidity }}
            </span>
        </div>
    </div>
</template>

<script lang="ts">
import type { JsonAdapter } from "inversify-utils-kit";
import { defineComponent, inject, reactive, toRefs } from "vue";

import CodeEditor from "@/components/utils/CodeEditor.vue";

export default defineComponent({
    components: { CodeEditor },
    setup() {
        const jsonAdapter = inject("jsonAdapter") as JsonAdapter;

        const state = reactive({
            input: "{}",
            result: "",
            reasonForInvalidity: "",
            isProcessing: false
        });

        const goToResultContainer = () => {
            const section = document.getElementById("json-formatter-result");
            if (section) {
                section.scrollIntoView({ behavior: "smooth" });
            }
        };

        const processFile = async () => {
            state.isProcessing = true;
            state.reasonForInvalidity = "";
            state.result = "";
            await new Promise((res) => setTimeout(res, 300));
            const jsonParsed = jsonAdapter.parseJsonStringToString(state.input);
            state.reasonForInvalidity = jsonParsed.reasonForInvalidity;
            if (!state.reasonForInvalidity) {
                state.result = jsonParsed.result;
                state.input = jsonParsed.result;
                goToResultContainer();
            }
            state.isProcessing = false;
        };

        return { processFile, ...toRefs(state) };
    }
});
</script>
